import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { TranslationService } from '../../../core/i18n/translation.service';
import {
  AuditExecutionChecklistDto,
  AuditExecutionDetailDto,
  AuditExecutionErrorDto,
  AuditExecutionItemDto,
  AuditExecutionItemType,
} from '../models/audit.models';
import { AuditsApi } from '../services/audits.api';
import {
  AuditAnswerSavedEvent,
  AuditExecutionAnswerComponent,
} from './audit-execution-answer.component';

@Component({
  selector: 'app-audit-execution',
  imports: [AuditExecutionAnswerComponent, ConfirmationDialogComponent],
  templateUrl: './audit-execution.component.html',
  host: { '(window:pagehide)': 'interruptExecution()', '(window:pageshow)': 'restoreExecution($event)' },
})
export class AuditExecutionComponent {
  protected readonly i18n = inject(TranslationService);
  private readonly auditsApi = inject(AuditsApi);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private loadRequestId = 0;
  private readonly executionCancelled = new Subject<void>();

  readonly planId = input.required<string>();
  readonly closeRequested = output<void>();
  readonly completed = output<void>();
  readonly previewRequested = output<string>();

  protected readonly execution = signal<AuditExecutionDetailDto | null>(null);
  protected readonly readOnly = computed(() => this.execution()?.status !== 'in_progress');
  protected readonly canRequestUnlock = computed(() => this.execution()?.can_request_unlock === true);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal('');
  protected readonly completionDialogOpen = signal(false);
  protected readonly isCompleting = signal(false);
  protected readonly completionError = signal('');
  protected readonly unlockRequestOpen = signal(false);
  protected readonly unlockReason = signal('');
  protected readonly unlockRequestBusy = signal(false);
  protected readonly unlockRequestMessage = signal('');
  protected readonly unlockRequestSent = signal(false);
  protected readonly missingItemIds = signal<string[]>([]);
  private readonly itemBusyById = signal<Record<string, boolean>>({});
  private readonly itemErrorById = signal<Record<string, boolean>>({});
  protected readonly questionIndex = signal(0);
  protected readonly groups = computed(() => (this.execution()?.checklists ?? []).flatMap(checklist =>
    checklist.groups.filter(group => group.items.length > 0).map(group => ({
      key: `${checklist.checklist_answer_id}:${group.id}`, checklist, group,
      answered: group.items.filter(item => this.isAnswerComplete(item)).length,
    })),
  ));
  protected readonly questions = computed(() => this.groups().flatMap((step, groupIndex) =>
    step.group.items.map((item, itemIndex) => ({ ...step, groupIndex, itemIndex, item, key: `${step.key}:${item.id}` })),
  ));
  protected readonly activeQuestion = computed(() => this.questions()[this.questionIndex()] ?? null);
  protected readonly isLastQuestion = computed(() => this.questionIndex() === this.questions().length - 1);
  protected readonly hasPendingItemChanges = computed(() =>
    Object.values(this.itemBusyById()).some(Boolean),
  );
  protected readonly totalItemCount = computed(() =>
    this.countItems(this.execution()?.checklists ?? []),
  );

  constructor() {
    effect(onCleanup => {
      const planId = this.planId();
      void this.loadExecution(planId);
      onCleanup(() => {
        ++this.loadRequestId;
        this.executionCancelled.next();
        this.auditsApi.pauseAuditExecution(planId).subscribe({ error: () => {} });
      });
    });
  }

  protected interruptExecution(): void {
    this.auditsApi.pauseAuditExecution(this.planId()).subscribe({ error: () => {} });
  }

  protected restoreExecution(event: PageTransitionEvent): void {
    if (event.persisted) void this.loadExecution(this.planId());
  }

  protected close(): void {
    this.closeRequested.emit();
  }

  protected toggleUnlockRequest(): void {
    if (this.unlockRequestSent()) return;
    this.unlockRequestOpen.update(value => !value);
    this.unlockRequestMessage.set('');
  }

  protected async requestUnlock(): Promise<void> {
    const reason = this.unlockReason().trim();
    if (!this.readOnly() || !reason || this.unlockRequestBusy() || this.unlockRequestSent()) return;
    this.unlockRequestBusy.set(true);
    this.unlockRequestMessage.set('');
    try {
      await firstValueFrom(this.auditsApi.requestAuditUnlock(this.planId(), reason));
      this.unlockRequestSent.set(true);
      this.unlockRequestOpen.set(false);
      this.unlockRequestMessage.set(this.i18n.t('auditExecution.preview.unlockSent'));
    } catch (error) {
      const code = error instanceof HttpErrorResponse ? error.error?.code : '';
      this.unlockRequestMessage.set(this.i18n.t(code === 'unlock_request_pending'
        ? 'auditUnlock.errors.pending'
        : code === 'unlock_recipient_missing'
          ? 'auditUnlock.errors.recipientMissing'
          : 'auditUnlock.errors.request'));
    } finally {
      this.unlockRequestBusy.set(false);
    }
  }

  protected retry(): void {
    void this.loadExecution(this.planId());
  }

  protected openCompletedExecution(planId: string): void {
    if (planId && planId !== this.planId()) this.previewRequested.emit(planId);
  }

  protected historyDate(value: string): string {
    return new Intl.DateTimeFormat(this.i18n.language() === 'pl' ? 'pl-PL' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  }

  protected getItemTypeLabel(itemType: AuditExecutionItemType): string {
    return this.i18n.t(`auditExecution.itemTypes.${itemType}`);
  }

  protected isAnswered(item: AuditExecutionItemDto): boolean {
    return this.isAnswerComplete(item);
  }

  protected isMissingItem(itemId: string): boolean {
    return this.missingItemIds().includes(itemId) || this.itemErrorById()[itemId] === true;
  }

  protected handleItemBusyChange(itemId: string, busy: boolean): void {
    this.itemBusyById.update((current) => ({ ...current, [itemId]: busy }));
  }

  protected handleItemErrorChange(itemId: string, failed: boolean): void {
    this.itemErrorById.update(current => ({ ...current, [itemId]: failed }));
    if (!Object.values(this.itemErrorById()).some(Boolean)
      && this.completionError() === this.i18n.t('auditExecution.navigation.saveError')) {
      this.completionError.set('');
    }
  }

  protected goToQuestion(index: number): void {
    if (this.isCompleting() || index < 0 || index >= this.questions().length) return;
    this.questionIndex.set(index);
    setTimeout(() => {
      const heading = this.host.nativeElement.querySelector<HTMLElement>('.audit-execution__question:not([hidden]) h5');
      heading?.focus({ preventScroll: true });
      this.host.nativeElement.querySelector('[aria-current="step"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  protected goToGroup(groupIndex: number): void {
    const questions = this.questions();
    const unanswered = questions.findIndex(question =>
      question.groupIndex === groupIndex && !this.isAnswerComplete(question.item),
    );
    this.goToQuestion(unanswered >= 0 ? unanswered : questions.findIndex(question => question.groupIndex === groupIndex));
  }

  protected openCompletionDialog(): void {
    if (this.readOnly() || this.isCompleting() || this.hasPendingItemChanges() || this.totalItemCount() === 0) {
      return;
    }
    const failed = this.questions().findIndex(question => this.itemErrorById()[question.item.id]);
    if (failed >= 0) {
      this.goToQuestion(failed);
      this.completionError.set(this.i18n.t('auditExecution.navigation.saveError'));
      return;
    }
    this.completionError.set('');
    this.completionDialogOpen.set(true);
  }

  protected cancelCompletion(): void {
    if (!this.isCompleting()) {
      this.completionDialogOpen.set(false);
    }
  }

  protected async confirmCompletion(): Promise<void> {
    if (this.isCompleting() || this.hasPendingItemChanges()) {
      return;
    }

    this.isCompleting.set(true);
    this.completionError.set('');
    this.missingItemIds.set([]);

    try {
      await firstValueFrom(this.auditsApi.completeAuditExecutionPlan(this.planId()));
      this.completionDialogOpen.set(false);
      this.completed.emit();
    } catch (error) {
      const completionError = this.parseCompletionError(error);
      this.missingItemIds.set(completionError.missingItemIds);
      this.completionError.set(completionError.message);
      this.completionDialogOpen.set(false);
      this.scrollToFirstMissingItem(completionError.missingItemIds);
    } finally {
      this.isCompleting.set(false);
    }
  }

  protected handleAnswerSaved(event: AuditAnswerSavedEvent): void {
    this.execution.update((execution) => {
      if (!execution) {
        return execution;
      }

      return {
        ...execution,
        checklists: execution.checklists.map((checklist) => ({
          ...checklist,
          groups: checklist.groups.map((group) => ({
            ...group,
            items: group.items.map((item) =>
              item.id === event.itemId ? { ...item, answer: event.answer } : item,
            ),
          })),
        })),
      };
    });

    const savedItem = this.execution()
      ?.checklists.flatMap((checklist) => checklist.groups)
      .flatMap((group) => group.items)
      .find((item) => item.id === event.itemId);
    const currentMissingItemIds = this.missingItemIds();
    if (
      savedItem &&
      this.isAnswerComplete(savedItem) &&
      currentMissingItemIds.includes(event.itemId)
    ) {
      const remainingItemIds = currentMissingItemIds.filter((itemId) => itemId !== event.itemId);
      this.missingItemIds.set(remainingItemIds);
      if (remainingItemIds.length === 0) {
        this.completionError.set('');
      }
    }
  }

  private async loadExecution(planId: string): Promise<void> {
    const requestId = ++this.loadRequestId;
    this.isLoading.set(true);
    this.loadError.set('');
    this.completionError.set('');
    this.missingItemIds.set([]);
    this.itemBusyById.set({});
    this.itemErrorById.set({});
    this.unlockRequestOpen.set(false);
    this.unlockReason.set('');
    this.unlockRequestMessage.set('');
    this.unlockRequestSent.set(false);
    this.questionIndex.set(0);
    this.execution.set(null);

    try {
      const execution = await firstValueFrom(this.auditsApi.getAuditExecution(planId).pipe(takeUntil(this.executionCancelled)));
      if (requestId === this.loadRequestId) {
        this.execution.set(execution);
        this.questionIndex.set(this.getResumeQuestionIndex(execution));
      }
    } catch (error) {
      if (requestId === this.loadRequestId) {
        this.loadError.set(this.resolveLoadError(error));
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading.set(false);
      }
    }
  }

  private getResumeQuestionIndex(execution: AuditExecutionDetailDto): number {
    if (execution.status !== 'in_progress') return 0;
    const items = execution.checklists.flatMap(checklist => checklist.groups.flatMap(group => group.items));
    const answered = items.map((item, index) => ({ item, index })).filter(({ item }) => this.isAnswerComplete(item));
    if (!answered.length) return 0;
    if (answered.length === items.length) return items.length - 1;

    let latestIndex = -1;
    let latestTime = -Infinity;
    for (const { item, index } of answered) {
      const updatedAt = Date.parse(item.answer!.updated_at);
      const answeredAt = Date.parse(item.answer!.answered_at);
      const timestamp = Number.isFinite(updatedAt) ? updatedAt : Number.isFinite(answeredAt) ? answeredAt : -Infinity;
      // Equal timestamps (e.g. imported answers) use the later question in API order.
      if (timestamp >= latestTime) {
        latestTime = timestamp;
        latestIndex = index;
      }
    }
    return Math.min(latestIndex + 1, items.length - 1);
  }

  private countItems(checklists: AuditExecutionChecklistDto[]): number {
    return checklists.reduce(
      (checklistTotal, checklist) =>
        checklistTotal +
        checklist.groups.reduce((groupTotal, group) => groupTotal + group.items.length, 0),
      0,
    );
  }

  private isAnswerComplete(item: AuditExecutionItemDto): boolean {
    const answer = item.answer;
    if (!answer) {
      return false;
    }

    switch (item.item_type) {
      case 'yes_no':
        return answer.boolean_value !== null;
      case 'scale':
      case 'number':
        return answer.numeric_value !== null;
      case 'number_with_unit':
        return answer.numeric_value !== null && Boolean(answer.unit?.trim());
      case 'multiscale':
        return Boolean(answer.selected_option_id?.trim());
      case 'multi_select':
        return answer.selected_option_ids.length > 0;
      case 'text':
        return Boolean(answer.text_value?.trim());
    }
  }

  private parseCompletionError(error: unknown): {
    message: string;
    missingItemIds: string[];
  } {
    if (error instanceof HttpErrorResponse) {
      const responseBody = this.getCompletionErrorBody(error);
      if (error.status === 409 && responseBody?.code === 'audit_execution_incomplete') {
        const missingItemIds = responseBody.missing_answers?.map((entry) => entry.item_id) ?? [];
        return {
          message: this.i18n.t('auditExecution.completion.errors.incomplete'),
          missingItemIds,
        };
      }
      if (error.status === 403) {
        return {
          message: this.i18n.t('auditExecution.completion.errors.forbidden'),
          missingItemIds: [],
        };
      }
      if (error.status === 404) {
        return {
          message: this.i18n.t('auditExecution.completion.errors.notFound'),
          missingItemIds: [],
        };
      }
      if (error.status === 409) {
        return {
          message: this.i18n.t('auditExecution.completion.errors.conflict'),
          missingItemIds: [],
        };
      }
    }
    return {
      message: this.i18n.t('auditExecution.completion.errors.complete'),
      missingItemIds: [],
    };
  }

  private getCompletionErrorBody(error: HttpErrorResponse): AuditExecutionErrorDto | null {
    if (typeof error.error !== 'object' || error.error === null) {
      return null;
    }
    return error.error as AuditExecutionErrorDto;
  }

  private scrollToFirstMissingItem(itemIds: string[]): void {
    const firstItemId = itemIds[0];
    if (!firstItemId) {
      return;
    }
    this.questionIndex.set(Math.max(0, this.questions().findIndex(question => question.item.id === firstItemId)));
    setTimeout(() => {
      document
        .getElementById(`audit-execution-item-${firstItemId}`)
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  }

  private resolveLoadError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.error?.code === "execution_plan_missed") return this.i18n.t("auditExecution.errors.windowClosed");
      if (error.error?.code === "future_execution_not_allowed") return this.i18n.t("auditExecution.errors.windowNotOpen");
      if (error.status === 403) {
        return this.i18n.t('auditExecution.errors.forbidden');
      }
      if (error.status === 404) {
        return this.i18n.t('auditExecution.errors.notFound');
      }
      if (error.status === 409) {
        return this.i18n.t('auditExecution.errors.notAvailable');
      }
    }
    return this.i18n.t('auditExecution.errors.load');
  }
}
